import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useNavigate } from 'react-router-dom'
import { 
  AlertTriangle, 
  X, 
  Zap, 
  ArrowRight,
  MessageSquare,
  CheckCircle
} from 'lucide-react'

interface UsageData {
  usage: {
    chatMessages: number
    codebases: number
    mcpServers: number
    storageUsed: number
  }
  limits: {
    chatMessages: number
    codebases: number
    mcpServers: number
    storage: number
  }
  percentages: {
    chatMessages: number
    codebases: number
    mcpServers: number
    storage: number
  }
  plan: string
  isWithinLimit: boolean
}

export default function UpgradeBanner() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [usage, setUsage] = useState<UsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Check if user is on Free or Basic plan (should see upgrade banner)
    const plan = user?.subscriptionTier
    if (plan !== 'free' && plan !== 'basic' && plan !== undefined) {
      setLoading(false)
      return
    }

    // Fetch usage data
    fetchUsage()
  }, [user])

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('halcode-auth')
      if (!token) return

      const parsed = JSON.parse(token)
      const accessToken = parsed.state?.accessToken || parsed.accessToken

      const response = await fetch('/api/subscription/usage', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUsage(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error)
    } finally {
      setLoading(false)
    }
  }

  // Don't show for non-free/basic users or if already dismissed
  const plan = user?.subscriptionTier
  if ((plan !== 'free' && plan !== 'basic' && plan !== undefined)) {
    return null
  }

  if (loading) {
    return null // Don't show loading state
  }

  if (dismissed) {
    return null
  }

  const messagesUsed = usage?.usage?.chatMessages || 0
  const messagesLimit = usage?.limits?.chatMessages || 25
  const percentage = usage?.percentages?.chatMessages || 0
  const isLowOnMessages = messagesUsed >= messagesLimit * 0.8

  return (
    <div className="relative bg-gradient-to-r from-primary-500/20 via-primary-600/20 to-purple-500/20 border border-primary-500/30 rounded-xl p-4 mb-4 overflow-hidden">
      {/* Background glow effect */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl" />

      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 text-dark-400 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
          <Zap className="w-5 h-5 text-primary-400" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white mb-1">
            {messagesUsed >= messagesLimit 
              ? "You've reached your daily limit!" 
              : "Upgrade to Pro for unlimited messages"}
          </h3>
          
          {/* Usage progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-dark-400 mb-1">
              <span>Messages used today</span>
              <span className={isLowOnMessages ? 'text-amber-400' : 'text-dark-300'}>
                {messagesUsed} / {messagesLimit}
              </span>
            </div>
            <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  percentage >= 90 
                    ? 'bg-red-500' 
                    : percentage >= 75 
                      ? 'bg-amber-500' 
                      : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          </div>

          {/* Features list */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-700/50 text-xs text-dark-300">
              <CheckCircle className="w-3 h-3 text-green-400" />
              Unlimited messages
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-700/50 text-xs text-dark-300">
              <CheckCircle className="w-3 h-3 text-green-400" />
              Multi-agent mode
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-700/50 text-xs text-dark-300">
              <CheckCircle className="w-3 h-3 text-green-400" />
              MCP servers
            </span>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => navigate('/billing')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <span>Upgrade to Pro - $19/mo</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
